import { AlertCircle, ArrowDownRight, ArrowUpRight, Minus, Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { CommandCentreKpi } from "@/api/intelligence";
import {
  formatKpiValue,
  formatPercentChange,
  formatSnapshotDate,
} from "@/lib/intelligenceFormat";
import { cn } from "@/lib/utils";

type Props = {
  kpi: CommandCentreKpi;
  comparisonLabel?: string | null;
};

function TrendIcon({ trend }: { trend: CommandCentreKpi["trend"] }) {
  if (trend === "up") return <ArrowUpRight className="h-4 w-4 text-emerald-600" />;
  if (trend === "down") return <ArrowDownRight className="h-4 w-4 text-rose-600" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

export function KpiCard({ kpi, comparisonLabel }: Props) {
  const unavailable = kpi.dataAvailability === "NOT_AVAILABLE";
  const limited = kpi.dataAvailability === "AVAILABLE_WITH_LIMITATIONS";

  const displayValue = unavailable ? "—" : formatKpiValue(kpi.unit, kpi.value);
  const prevDisplay =
    kpi.previousValue !== null && !unavailable
      ? formatKpiValue(kpi.unit, kpi.previousValue)
      : "—";

  return (
    <Card className="overflow-hidden border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground truncate">
              {kpi.displayName}
            </p>
            {kpi.key === "outstanding" && kpi.snapshotAsOf && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                As of {formatSnapshotDate(kpi.snapshotAsOf)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {limited && (
              <span title={kpi.limitations ?? "Available with data limitations"}>
                <Info className="h-3.5 w-3.5 text-amber-600" aria-label="Limitation" />
              </span>
            )}
            {unavailable && <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
        </div>

        <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
          {displayValue}
        </p>

        {!unavailable && kpi.supportsComparison && (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <TrendIcon trend={kpi.trend} />
            <span
              className={cn(
                "font-medium tabular-nums",
                kpi.trend === "up" && "text-emerald-700",
                kpi.trend === "down" && "text-rose-700"
              )}
            >
              {formatPercentChange(kpi.percentChange)}
            </span>
            <span className="truncate">vs {comparisonLabel ?? "prior period"}</span>
          </div>
        )}

        {!unavailable && kpi.previousValue !== null && (
          <p className="mt-1 text-[11px] text-muted-foreground tabular-nums">
            Previous: {prevDisplay}
          </p>
        )}

        {unavailable && (
          <p className="mt-2 text-xs text-muted-foreground">
            {kpi.limitations ?? "Data unavailable"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
